// =====================================================================
// CardsTab (Financeiro Cartões / S63) — previsão de recebimento da maquininha.
// Cada pagamento por débito/crédito vira parcelas (líquido + data D+N). Marcar
// "recebido" lança o líquido na conta da Tesouraria. Componente auto-contido.
// =====================================================================
import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box, Button, Chip, CircularProgress, MenuItem, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions
} from "@material-ui/core";
import SettingsIcon from "@material-ui/icons/Settings";
import UndoIcon from "@material-ui/icons/Undo";
import { toast } from "react-toastify";
import {
  listCardReceivables, receiveCardParcel, unreceiveCardParcel, CARD_BRANDS, fmtMoney, financeErrorMessage
} from "../../services/financeService";
import CardFeeRulesDialog from "./CardFeeRulesDialog";
import useIsMobile from "../../utils/useIsMobile";

const useStyles = makeStyles((theme) => ({
  summary: { display: "flex", gap: theme.spacing(2), flexWrap: "wrap", margin: theme.spacing(1, 0, 2) },
  card: { padding: theme.spacing(1.5, 2), minWidth: 160, flex: "1 1 140px", border: `1px solid ${theme.palette.divider}`, borderRadius: 8 },
  cardValue: { fontWeight: 700, fontSize: "1.2rem" },
  toolbar: { display: "flex", gap: theme.spacing(2), alignItems: "center", flexWrap: "wrap", marginBottom: theme.spacing(2) },
  empty: { padding: theme.spacing(6), textAlign: "center", color: theme.palette.text.secondary },
  loading: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 160 }
}));

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "—";
};

const CardsTab = ({ accounts = [], onChanged }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const active = accounts.filter((a) => a.active);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [data, setData] = useState({ receivables: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [receiveAccountId, setReceiveAccountId] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      setData(await listCardReceivables({ from: from || undefined, to: to || undefined, status: statusFilter || undefined }));
    } catch (e) {
      toast.error("Falha ao carregar os cartões.");
      setData({ receivables: [], summary: {} });
    } finally { setLoading(false); }
  }, [from, to, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (receiveTarget) setReceiveAccountId(String((active.find((a) => a.isDefault) || active[0] || {}).id || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiveTarget]);

  const brandLabel = (b) => (CARD_BRANDS.find((x) => x.value === b) || {}).label || b || "—";

  const handleReceive = async () => {
    const p = receiveTarget;
    try {
      await receiveCardParcel(p.id, receiveAccountId || undefined);
      toast.success("Parcela recebida — líquido lançado na conta.");
      setReceiveTarget(null);
      fetchData();
      onChanged && onChanged();
    } catch (err) { toast.error(financeErrorMessage(err)); }
  };
  const handleUnreceive = async (p) => {
    try { await unreceiveCardParcel(p.id); fetchData(); onChanged && onChanged(); }
    catch (err) { toast.error(financeErrorMessage(err)); }
  };

  const s = data.summary || {};

  return (
    <>
      <div className={classes.summary}>
        <div className={classes.card}>
          <Typography variant="caption" color="textSecondary">A receber (líquido)</Typography>
          <Typography className={classes.cardValue}>{fmtMoney(s.previstoNet || 0)}</Typography>
          <Typography variant="caption" color="textSecondary">{s.previstoCount || 0} parcela(s)</Typography>
        </div>
        <div className={classes.card}>
          <Typography variant="caption" color="textSecondary">Recebido (líquido)</Typography>
          <Typography className={classes.cardValue} style={{ color: "#2e7d32" }}>{fmtMoney(s.recebidoNet || 0)}</Typography>
        </div>
        <div className={classes.card}>
          <Typography variant="caption" color="textSecondary">Total em taxas</Typography>
          <Typography className={classes.cardValue} style={{ color: "#c62828" }}>{fmtMoney(s.totalFee || 0)}</Typography>
        </div>
      </div>

      <div className={classes.toolbar}>
        <TextField type="date" size="small" variant="outlined" label="De" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="date" size="small" variant="outlined" label="Até" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField select size="small" variant="outlined" label="Filtro" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ minWidth: 140 }}>
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="previsto">A receber</MenuItem>
          <MenuItem value="recebido">Recebidas</MenuItem>
        </TextField>
        <Box flexGrow={1} />
        <Button size="small" startIcon={<SettingsIcon />} onClick={() => setRulesOpen(true)}>Regras de taxa</Button>
      </div>

      {loading ? (
        <div className={classes.loading}><CircularProgress /></div>
      ) : (data.receivables || []).length === 0 ? (
        <div className={classes.empty}>
          <Typography>Nenhum recebível de cartão. Eles aparecem quando você registra um pagamento por débito/crédito.</Typography>
        </div>
      ) : (
        <div style={{ overflowX: "auto", width: "100%" }}>
        <Table size="small" style={{ minWidth: 640 }}>
          <TableHead><TableRow>
            <TableCell>Previsão</TableCell><TableCell>Paciente</TableCell><TableCell>Bandeira</TableCell>
            <TableCell align="center">Parcela</TableCell><TableCell align="right">Bruto</TableCell>
            <TableCell align="right">Taxa</TableCell><TableCell align="right">Líquido</TableCell>
            <TableCell align="center">Status</TableCell><TableCell align="center" style={{ width: 110 }}>Ações</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {data.receivables.map((r) => {
              const received = r.status === "recebido";
              return (
                <TableRow key={r.id} hover>
                  <TableCell>{fmtDate(r.dueDate)}</TableCell>
                  <TableCell>{r.patientName || "—"}</TableCell>
                  <TableCell>{brandLabel(r.brand)} <Typography variant="caption" color="textSecondary">{r.kind === "credito" ? "créd" : "déb"}</Typography></TableCell>
                  <TableCell align="center">{r.parcelNumber}/{r.installmentsTotal}</TableCell>
                  <TableCell align="right">{fmtMoney(r.grossAmount)}</TableCell>
                  <TableCell align="right" style={{ color: "#c62828" }}>{fmtMoney(r.feeAmount)}</TableCell>
                  <TableCell align="right" style={{ fontWeight: 600 }}>{fmtMoney(r.netAmount)}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={received ? "Recebida" : "A receber"}
                      style={{ backgroundColor: received ? "#2e7d32" : "#b26a00", color: "#fff" }} />
                  </TableCell>
                  <TableCell align="center">
                    {received ? (
                      <Tooltip title="Desfazer recebimento"><IconButton size="small" onClick={() => handleUnreceive(r)}><UndoIcon fontSize="small" /></IconButton></Tooltip>
                    ) : (
                      <Button size="small" color="primary" variant="outlined" onClick={() => setReceiveTarget(r)}>Receber</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </div>
      )}

      <CardFeeRulesDialog open={rulesOpen} onClose={() => { setRulesOpen(false); fetchData(); }} />

      <Dialog open={!!receiveTarget} onClose={() => setReceiveTarget(null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Receber parcela</DialogTitle>
        <DialogContent>
          {receiveTarget && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Líquido <strong>{fmtMoney(receiveTarget.netAmount)}</strong> (parcela {receiveTarget.parcelNumber}/{receiveTarget.installmentsTotal}).
              Será lançado como entrada na conta escolhida.
            </Typography>
          )}
          {active.length > 0 ? (
            <TextField select label="Conta (onde caiu)" value={receiveAccountId} onChange={(e) => setReceiveAccountId(e.target.value)} fullWidth margin="dense">
              {active.map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>)}
            </TextField>
          ) : (
            <Typography variant="caption" color="textSecondary">Sem contas cadastradas — será criada/usada a conta "Caixa" padrão.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveTarget(null)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleReceive}>Confirmar recebimento</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CardsTab;
